/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from 'react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { DiscountCardIcon } from './discount_card_icon'
import { calendarTheme } from './calendar_theme'

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const RGB_COLOR_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i
const HSL_COLOR_PATTERN = /^hsla?\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i

const clampChannel = (value) => Math.min(255, Math.max(0, Math.round(Number(value))))
const clampAlpha = (value) => Math.min(1, Math.max(0, Number(value)))

const hslToRgb = (hue, saturation, lightness) => {
  const normalizedHue = ((Number(hue) % 360) + 360) % 360
  const normalizedSaturation = Math.min(100, Math.max(0, Number(saturation))) / 100
  const normalizedLightness = Math.min(100, Math.max(0, Number(lightness))) / 100

  if (normalizedSaturation === 0) {
    const channel = Math.round(normalizedLightness * 255)
    return { r: channel, g: channel, b: channel }
  }

  const chroma = (1 - Math.abs((2 * normalizedLightness) - 1)) * normalizedSaturation
  const hueSection = normalizedHue / 60
  const secondComponent = chroma * (1 - Math.abs((hueSection % 2) - 1))
  const matchLightness = normalizedLightness - (chroma / 2)

  let red = 0
  let green = 0
  let blue = 0

  if (hueSection >= 0 && hueSection < 1) {
    red = chroma
    green = secondComponent
  } else if (hueSection < 2) {
    red = secondComponent
    green = chroma
  } else if (hueSection < 3) {
    green = chroma
    blue = secondComponent
  } else if (hueSection < 4) {
    green = secondComponent
    blue = chroma
  } else if (hueSection < 5) {
    red = secondComponent
    blue = chroma
  } else {
    red = chroma
    blue = secondComponent
  }

  return {
    r: Math.round((red + matchLightness) * 255),
    g: Math.round((green + matchLightness) * 255),
    b: Math.round((blue + matchLightness) * 255),
  }
}

const parseColor = (color) => {
  const normalizedColor = `${color || ''}`.trim().toLowerCase()

  if (!normalizedColor) {
    return null
  }

  if (normalizedColor === 'white') {
    return { r: 255, g: 255, b: 255, a: 1 }
  }

  const normalizedWithoutSpaces = normalizedColor.replace(/\s+/g, '')

  if (HEX_COLOR_PATTERN.test(normalizedWithoutSpaces)) {
    let hexValue = normalizedWithoutSpaces.slice(1)

    if (hexValue.length === 3 || hexValue.length === 4) {
      hexValue = hexValue.split('').map((character) => `${character}${character}`).join('')
    }

    const hasAlpha = hexValue.length === 8
    return {
      r: parseInt(hexValue.slice(0, 2), 16),
      g: parseInt(hexValue.slice(2, 4), 16),
      b: parseInt(hexValue.slice(4, 6), 16),
      a: hasAlpha ? parseInt(hexValue.slice(6, 8), 16) / 255 : 1,
    }
  }

  const rgbMatch = normalizedColor.match(RGB_COLOR_PATTERN)
  if (rgbMatch) {
    return {
      r: clampChannel(rgbMatch[1]),
      g: clampChannel(rgbMatch[2]),
      b: clampChannel(rgbMatch[3]),
      a: rgbMatch[4] !== undefined ? clampAlpha(rgbMatch[4]) : 1,
    }
  }

  const hslMatch = normalizedColor.match(HSL_COLOR_PATTERN)
  if (hslMatch) {
    return {
      ...hslToRgb(hslMatch[1], hslMatch[2], hslMatch[3]),
      a: hslMatch[4] !== undefined ? clampAlpha(hslMatch[4]) : 1,
    }
  }

  return null
}

const toRgbaString = ({ r, g, b, a = 1 }) => `rgba(${r}, ${g}, ${b}, ${a})`

export const isWhiteCardColor = (color) => {
  const parsedColor = parseColor(color)

  return Boolean(parsedColor) && parsedColor.r === 255 && parsedColor.g === 255 && parsedColor.b === 255
}

export const getChipColors = (color) => {
  const parsedColor = parseColor(color)

  if (!parsedColor) {
    return {
      backgroundColor: calendarTheme.colors.background,
      borderColor: '#d1d5db',
    }
  }

  if (isWhiteCardColor(color)) {
    return {
      backgroundColor: toRgbaString(parsedColor),
      borderColor: '#d1d5db',
    }
  }

  return {
    backgroundColor: `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, 0.12)`,
    borderColor: toRgbaString(parsedColor),
  }
}

const renderCardChips = (cards, emptyMessage) => {
  if (!cards?.length) {
    return (
      <Typography sx={{ fontSize: '13px', color: calendarTheme.colors.mutedText }}>
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {cards.map((card) => {
        const chipColors = getChipColors(card?.color)

        return (
          <Chip
            key={card?.id}
            label={card?.name}
            size="small"
            sx={{
              borderRadius: '10px',
              backgroundColor: chipColors.backgroundColor,
              border: `1px solid ${chipColors.borderColor}`,
              color: calendarTheme.colors.headingText,
              fontWeight: 600,
            }}
          />
        )
      })}
    </Stack>
  )
}

export const EventDetailDialog = ({ occurrence, onClose, onNavigateToDiscounts }) => {
  const handleEditClick = () => {
    onClose()
    onNavigateToDiscounts?.()
  }

  return (
    <Dialog
      open={Boolean(occurrence)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="dynamic-discounts-calendar-event-dialog"
      PaperProps={{
        sx: {
          borderRadius: '18px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEditClick}
            data-testid="dynamic-discounts-calendar-edit-button"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              color: calendarTheme.colors.subheadingText,
              borderColor: calendarTheme.colors.gridBorder,
            }}
          >
            Edit
          </Button>
        </Stack>

        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '0 !important', pb: 3 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.15, mb: 0.75 }}>
              {occurrence?.title || 'Dynamic discount event'}
            </Typography>
            <Typography sx={{ fontSize: '14px', color: calendarTheme.colors.mutedText }}>
              {occurrence?.date ? moment(occurrence.date).format('dddd, MMMM D') : ''}
              {occurrence?.slotLabel ? `  •  ${occurrence.slotLabel}` : ''}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '10px',
                  backgroundColor: calendarTheme.colors.iconBadgeBackground,
                }}
              >
                <DiscountCardIcon size={18} borderColor={calendarTheme.colors.iconBadgeStroke} strokeColor={calendarTheme.colors.iconBadgeStroke} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: calendarTheme.colors.subheadingText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Affected cards
              </Typography>
            </Stack>
            {renderCardChips(occurrence?.blockedCards, 'No cards are currently blocked for this event.')}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

